import React, { useEffect, useMemo, useState } from "react";
import type { NoteTypeDto, CreateNoteTypeDto, UpdateNoteTypeDto } from "../models/Note";
import { useApiClient } from '../components/useApiClient';
import useAuthToken from '../functions/useAuthToken';
export default function AdminNoteTypesPage() {
    //API
    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();

    const [items, setItems] = useState<NoteTypeDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Add form
    const [newName, setNewName] = useState("");
    const [newSortOrder, setNewSortOrder] = useState<number>(0);

    // Edit modal
    const [editTarget, setEditTarget] = useState<NoteTypeDto | null>(null);
    const [editName, setEditName] = useState("");
    const [editSortOrder, setEditSortOrder] = useState<number>(0);
    const [saving, setSaving] = useState(false);

    // Delete modal
    const [deleteTarget, setDeleteTarget] = useState<NoteTypeDto | null>(null);
    const [deleting, setDeleting] = useState(false);

    const sorted = useMemo(() => {
        return [...items].sort((a, b) =>
            a.sortOrder !== b.sortOrder ? a.sortOrder - b.sortOrder : a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );
    }, [items]);

    //const maxSortOrder = useMemo(() => {
    //    if (items.length === 0) return 0;

    //    return Math.max(...items.map(nt => nt.sortOrder));
    //}, [items]);

    useEffect(() => {
        if (items.length === 0) return;
        if (newSortOrder !== 0) return;

        const max = Math.max(...items.map(nt => nt.sortOrder));
        setNewSortOrder(max + 10);
    }, [items]);

    async function adminNoteTypesApi(
        method: 'list' | 'create' | 'update' | 'remove',
        id?: number,
        dto?: any
    ): Promise<NoteTypeDto[] | NoteTypeDto | null> {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Invalid access token');

        switch (method) {
            case 'list':
                return await get<NoteTypeDto[]>("admin/note-types", accessToken);
            case 'create':
                return await postJson<NoteTypeDto>("admin/note-types", accessToken, JSON.stringify(dto));
            case 'update':
                return await putJson<NoteTypeDto>(`admin/note-types/${id}`, accessToken, JSON.stringify(dto));
            case 'remove':
                return await del(`admin/note-types/${id}`, accessToken);
            default: return null;
        }
    };

    async function load() {
        setError(null);
        setLoading(true);
        try {
            const data = await adminNoteTypesApi("list");
            if (data && Array.isArray(data)) {
                setItems(data);
            }
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to load note types.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { void load(); }, []);

    async function addNoteType() {
        const name = newName.trim();
        if (!name) {
            setError("Name is required.");
            return;
        }

        setError(null);
        try {
            const dto: CreateNoteTypeDto = {
                name: name,
                sortOrder: Number(newSortOrder) || 0,
            }

            const created = await adminNoteTypesApi("create", undefined, dto);

            if (created && !Array.isArray(created)) {
                setItems(prev => [...prev, created]);
            }

            setNewName("");
            setNewSortOrder(0);
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to create note type.");
        }
    }

    function openEdit(item: NoteTypeDto) {
        setEditTarget(item);
        setEditName(item.name);
        setEditSortOrder(item.sortOrder);
        setError(null);
    }

    function closeEdit() {
        setEditTarget(null);
        setSaving(false);
    }

    async function saveEdit() {
        if (!editTarget) return;

        const name = editName.trim();
        if (!name) {
            setError("Name is required.");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const dto: UpdateNoteTypeDto = {
                name: name,
                sortOrder: Number(editSortOrder) || 0,
            }

            const updated = await adminNoteTypesApi("update", editTarget.noteTypeId, dto);

            if (updated && !Array.isArray(updated)) {
                setItems(prev => prev.map(x => (x.noteTypeId === updated.noteTypeId ? updated : x)));
            }

            closeEdit();
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to update note type.");
        } finally {
            setSaving(false);
        }
    }

    async function confirmDelete() {
        if (!deleteTarget) return;

        setDeleting(true);
        setError(null);

        try {
            await adminNoteTypesApi("remove", deleteTarget.noteTypeId);
            setItems(prev => prev.filter(x => x.noteTypeId !== deleteTarget.noteTypeId));
            setDeleteTarget(null);
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 409) setError(e?.response?.data?.detail ?? "Cannot delete: note type is in use.");
            else setError(e?.response?.data?.detail ?? e?.message ?? "Failed to delete note type.");
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div style={{ padding: 24, maxWidth: 1000 }}>
            <h2>Admin · Note Types</h2>
            <p>System note types can’t be deleted.</p>

            {error && (
                <div style={{ marginTop: 12, padding: 12, border: "1px solid #ddd" }}>
                    {error}
                </div>
            )}

            {/* Add */}
            <div style={{ marginTop: 16, border: "1px solid #eee", padding: 12 }}>
                <h3 style={{ marginTop: 0 }}>Add Custom Note Type & Set Sort Order</h3>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 140px", gap: 8 }}>
                    <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name" />
                    <input
                        type="number"
                        value={newSortOrder}
                        onChange={e => setNewSortOrder(Number(e.target.value))}
                        placeholder="SortOrder"
                    />
                    <button onClick={() => void addNoteType()} disabled={!newName.trim()}>
                        Add
                    </button>
                </div>
            </div>

            {/* List */}
            <div style={{ marginTop: 16 }}>
                {loading ? (
                    <div>Loading…</div>
                ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={th}>Name</th>
                                <th style={th}>Sort</th>
                                <th style={{ ...th, width: 220 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(nt => (
                                <tr key={nt.noteTypeId}>
                                    <td style={td}>{nt.name}</td>
                                    <td style={td}>{nt.sortOrder}</td>
                                    <td style={{ ...td, textAlign: "right" }}>
                                        <button onClick={() => openEdit(nt)} style={{ marginRight: 8 }}>
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => setDeleteTarget(nt)}
                                            disabled={nt.isSystem}
                                            title={nt.isSystem ? "System note types cannot be deleted" : "Delete"}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {sorted.length === 0 && (
                                <tr><td colSpan={5} style={{ padding: 12 }}>No note types found.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Edit modal */}
            {editTarget && (
                <div style={overlayStyle}>
                    <div style={modalStyle}>
                        <h3>Edit Note Type</h3>
                        <p style={{ marginTop: 0 }}>
                            Edit <strong>Name</strong> and set <strong>Sort Order</strong> for where type appears in dropdown when editing
                        </p>

                        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8 }}>
                            <div>
                                <label>Name</label>
                                <input value={editName} onChange={e => setEditName(e.target.value)} style={{ width: "100%" }} />
                            </div>
                            <div>
                                <label>Sort Order</label>
                                <input
                                    type="number"
                                    value={editSortOrder}
                                    onChange={e => setEditSortOrder(Number(e.target.value))}
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: 16, textAlign: "right" }}>
                            <button onClick={closeEdit} disabled={saving} style={{ marginRight: 8 }}>
                                Cancel
                            </button>
                            <button onClick={() => void saveEdit()} disabled={saving}>
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete modal */}
            {deleteTarget && (
                <div style={overlayStyle}>
                    <div style={modalStyle}>
                        <h3>Delete Note Type</h3>
                        <p>
                            Delete "<strong>{deleteTarget.name}</strong>"?
                        </p>
                        <div style={{ textAlign: "right" }}>
                            <button onClick={() => setDeleteTarget(null)} disabled={deleting} style={{ marginRight: 8 }}>
                                Cancel
                            </button>
                            <button
                                onClick={() => void confirmDelete()}
                                disabled={deleting || deleteTarget.isSystem}
                                title={deleteTarget.isSystem ? "System note types cannot be deleted" : "Delete"}
                            >
                                {deleting ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const th: React.CSSProperties = { textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 };
const td: React.CSSProperties = { borderBottom: "1px solid #eee", padding: 8 };

const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
    background: "white",
    padding: 24,
    borderRadius: 8,
    width: 640,
    maxWidth: "95%",
};
